import { useAuthStore } from "../store/authStore";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { surveysService } from "../services/surveys";

export default function DashboardPage() {
  const { user } = useAuthStore();

  // Fetch active funded surveys
  const { data: surveysData, isLoading: surveysLoading } = useQuery({
    queryKey: ["surveys", "active"],
    queryFn: () => surveysService.list({ status: "active" }),
  });

  const activeSurveys = surveysData?.surveys?.filter(s => s.status === "active") || [];

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-100 mb-2">
        Welcome back, {user?.email?.split("@")[0] || user?.username || "there"} 👋
      </h1>
      <p className="text-gray-400 mb-8">
        Logged in as <span className="text-primary-300 capitalize">{user?.role?.replace("_", " ")}</span>
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-300 mb-2">Your Role</h3>
          <p className="text-2xl font-bold text-primary-300 capitalize">
            {user?.role?.replace("_", " ")}
          </p>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-300 mb-2">Status</h3>
          <p className="text-2xl font-bold capitalize text-green-500">{user?.status || "Active"}</p>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-300 mb-2">Email Verified</h3>
          <p className="text-2xl font-bold">{user?.email_verified ? "✓ Yes" : "✗ No"}</p>
        </div>
      </div>

      {/* Active Surveys */}
      {!surveysLoading && activeSurveys.length > 0 && (
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-100">Active Surveys</h2>
            <Link to="/surveys" className="text-primary-300 hover:text-primary-200 text-sm">
              View All →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activeSurveys.slice(0, 6).map((survey) => (
              <Link
                key={survey.id}
                to={`/surveys/${survey.id}`}
                className="card hover:border-primary-500 transition-all"
              >
                <h3 className="text-xl font-bold text-gray-100 mb-2">{survey.title}</h3>
                {survey.description && (
                  <p className="text-gray-400 mb-4 line-clamp-2">{survey.description}</p>
                )}
                <div className="space-y-1 text-sm text-gray-400">
                  <p>Budget: <span className="text-gray-300">{survey.budget} points</span></p>
                  <p>Responses: <span className="text-gray-300">{survey.actual_responses} / {survey.target_responses}</span></p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <h2 className="text-xl font-bold text-gray-100 mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <Link to="/surveys" className="block btn btn-primary text-center">
              Browse Surveys
            </Link>
            {(user?.role === "admin" || user?.role === "user" || user?.role === "agent" || user?.role === "question_owner") && (
              <Link to="/surveys/create" className="block btn btn-secondary text-center">
                Create Survey
              </Link>
            )}
            <Link to="/deposit" className="block btn btn-secondary text-center">
              Deposit Points
            </Link>
            <Link to="/redeem" className="block btn btn-secondary text-center">
              Redeem Points
            </Link>
            {(user?.role === "user" || user?.role === "agent" || user?.role === "responder") && (
              <Link to="/profile" className="block btn btn-secondary text-center">
                View Profile
              </Link>
            )}
            {user?.role === "admin" && (
              <Link to="/admin" className="block btn btn-secondary text-center">
                Admin Panel
              </Link>
            )}
          </div>
        </div>

        <div className="card">
          <h2 className="text-xl font-bold text-gray-100 mb-4">Getting Started</h2>
          <ul className="space-y-2 text-gray-400">
            <li>• Complete your profile to get better survey matches</li>
            <li>• Purchase points to fund surveys</li>
            <li>• Earn rewards by responding to surveys</li>
            <li>• Check your profile for funding and response history</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
